/**
 * InviteWelcome - Landing page for public invite flow
 * Route: /i/:token
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInvite, parseRpcError } from '@shared/utils/publicRpc';
import { useInviteToken, buildInvitePath } from '@shared/utils/inviteToken';

export default function InviteWelcome() {
  const token = useInviteToken();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    async function fetchInvite() {
      try {
        setLoading(true);
        setError(null);

        const data = await getInvite(token);
        
        if (!data) {
          setError('Invalid or expired invite link. Please check your email for the correct link.');
          return;
        }

        setInvite(data);
      } catch (err) {
        setError(parseRpcError(err));
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      fetchInvite();
    }
  }, [token]);

  const handleStartInduction = () => {
    // For Phase 2: Skip verification if not required
    // In future: check if code verification is needed
    navigate(buildInvitePath(token, 'induction'));
  };

  const handleVerifyCode = () => {
    navigate(buildInvitePath(token, 'verify'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading your invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Unable to Load Invite</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-6">
            <a
              href="mailto:support@example.com"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!invite) {
    return null;
  }

  // Check if already submitted
  if (invite.status === 'submitted') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Already Submitted</h2>
          <p className="mt-2 text-gray-600">
            Your induction has already been submitted and is under review.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Welcome header */}
      <div className="bg-blue-50 px-8 py-6 border-b border-blue-100">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome, {invite.person_name}!</h1>
            <p className="text-sm text-gray-600 mt-1">You've been invited to complete a site induction</p>
          </div>
        </div>
      </div>

      {/* Invite details */}
      <div className="px-8 py-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Induction Details</h2>
          
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Brief:</dt>
              <dd className="text-sm text-gray-900 font-medium">{invite.brief_title}</dd>
            </div>
            
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Your Email:</dt>
              <dd className="text-sm text-gray-900">{invite.person_email}</dd>
            </div>
            
            {invite.person_company && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Company:</dt>
                <dd className="text-sm text-gray-900">{invite.person_company}</dd>
              </div>
            )}

            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Status:</dt>
              <dd>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {invite.status}
                </span>
              </dd>
            </div>

            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Expires:</dt>
              <dd className="text-sm text-gray-900">
                {new Date(invite.expires_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </dd>
            </div>
          </dl>
        </div>

        {/* Important notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Before you start</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Please allow 10-15 minutes to complete the induction</li>
                  <li>You'll need to provide a digital signature</li>
                  <li>Ensure you have a stable internet connection</li>
                  <li>Read each section carefully before proceeding</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={handleStartInduction}
            className="flex-1 inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Start Induction
          </button>
          
          {/* Optional: Verify code button (for future use) */}
          {/*
          <button
            onClick={handleVerifyCode}
            className="flex-1 inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Verify Security Code
          </button>
          */}
        </div>
      </div>
    </div>
  );
}
